import React from "react";
import { Box, Typography } from "@mui/material";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";
import { getChevronPosition } from "../utils/chartHelpers";

export type TrendChartSummarySectionProps = {
  activeIndex: number;
  dataLength: number;
  chevronLeftMargin: number;
  chevronRightMargin: number;
  /**
   * When true (default), the chevron shows only if there is more than one data point.
   * Vitals charts pass false so the chevron shows whenever a point is active.
   */
  chevronOnlyWithMultipleDataPoints?: boolean;
  /** Localized date label (caller runs `t(...)`) */
  datePrefix: string;
  fullDate: string;
  children: React.ReactNode;
};

/**
 * Chevron + grey summary card (date + value row) used under trend line charts.
 */
export function TrendChartSummarySection({
  activeIndex,
  dataLength,
  chevronLeftMargin,
  chevronRightMargin,
  chevronOnlyWithMultipleDataPoints = true,
  datePrefix,
  fullDate,
  children,
}: TrendChartSummarySectionProps) {
  const showChevron =
    activeIndex !== -1 &&
    (!chevronOnlyWithMultipleDataPoints || dataLength > 1);

  return (
    <div style={{ position: "relative" }}>
      {showChevron && (
        <div
          style={{
            position: "absolute",
            left: getChevronPosition(
              activeIndex,
              dataLength,
              chevronLeftMargin,
              chevronRightMargin
            ),
            top: -8,
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderBottom: `8px solid ${theme.palette.grey[200]}`,
            zIndex: 10,
          }}
        />
      )}

      <Box
        sx={{
          padding: convertPxToRem(16),
          background: theme.palette.grey[200],
          borderRadius: convertPxToRem(10),
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: theme.palette.grey[700],
          }}
        >
          <span style={{ paddingRight: 3 }}>{datePrefix}</span>
          <span>{fullDate}</span>
        </Typography>
        {children}
      </Box>
    </div>
  );
}
